import { useState } from "react"

import { Link } from "react-router-dom"

import {
  isRefreshPaymentRequiredError,
  REFRESH_PAYMENT_REQUIRED_MESSAGE,
} from "~/api/battery-financing-errors"
import { sungageApplicationErrorMessage } from "~/api/battery-sungage-application-errors"
import type {
  BatteryProposalDto,
  BatteryProposalFinancingDto,
  BatteryQuoteDto,
} from "~/api/dto/battery-proposal.dto"
import useStartBatterySungageApplication from "~/api/hooks/useStartBatterySungageApplication"

import { isBatteryProposalReadOnly } from "../battery-proposal-read-only"
import {
  SUNGAGE_RESERVE_NOW_BUSY_LABEL,
  SUNGAGE_UNCERTAIN_PROMPT,
} from "./sungage-battery-copy"
import { SungageHostedApplication } from "./SungageHostedApplication"
import { RESERVE_NOW_LABEL } from "../contract/contract-action"
import { APPLICATION_STATUS_ACTION_LABEL } from "../financing-status/application-status-labels"
import type { BatteryDrawerLaunchState } from "../financing-status/battery-drawer-launch"
import { SelectLoanProductAction } from "../select-loan-product-action"
import {
  batteryFinancingStatusPath,
  batteryProposalPath,
} from "../setup/setup-steps"

const pillClass = `font-display inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-[length:var(--step-0)] font-bold text-bp-accent shadow-[0_12px_32px_rgba(0,0,0,0.25)] hover:bg-white/90 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60`

const ApplicationStatusPill = ({
  proposalId,
}: {
  proposalId: string
}): JSX.Element => (
  <Link
    to={batteryFinancingStatusPath(proposalId)}
    state={
      {
        returnTo: batteryProposalPath(proposalId),
      } satisfies BatteryDrawerLaunchState
    }
    data-testid="battery-financial-summary-application-status"
    className={pillClass}
  >
    {APPLICATION_STATUS_ACTION_LABEL}
    <span aria-hidden="true">→</span>
  </Link>
)

/**
 * Reserve now for Sungage BrightStart. The iframe opens only after the
 * server returns a borrower application URL. After any Existing Loan
 * Application the entry becomes Application status.
 */
export function SungageBatteryReserveNow({
  proposal,
  quote,
  financing,
  needsLoanProduct,
}: {
  proposal: BatteryProposalDto
  quote: BatteryQuoteDto | null
  financing: BatteryProposalFinancingDto
  needsLoanProduct: boolean
}): JSX.Element | null {
  const proposalId = proposal.id
  const start = useStartBatterySungageApplication()
  const [idempotencyKey] = useState(() => crypto.randomUUID())
  const [hostedUrl, setHostedUrl] = useState<string | null>(null)

  const hasApplication =
    financing.currentApplication?.provider === `sungage` ||
    financing.previousApplications.some(
      (application) => application.provider === `sungage`,
    )
  const uncertain =
    !hasApplication &&
    start.isSuccess &&
    start.data.applicationUrl == null &&
    start.data.application.reviewNeeded

  if (isBatteryProposalReadOnly(proposal) && !hasApplication) {
    return null
  }

  if (!hasApplication && needsLoanProduct) {
    return <SelectLoanProductAction proposalId={proposalId} />
  }

  const startAction = financing.actions.startSungageApplication
  const canStart =
    !hasApplication &&
    (startAction?.allowed ?? false) &&
    quote?.id != null &&
    quote.status === `current` &&
    !isBatteryProposalReadOnly(proposal)
  const pending = start.isLoading
  const reason =
    hasApplication || canStart
      ? null
      : (startAction?.reason ??
        (quote?.status === `current`
          ? null
          : `Save a current BrightStart Quote before applying`))

  const onReserve = (): void => {
    if (!canStart || pending || quote?.id == null) {
      return
    }
    start.mutate(
      {
        proposalId,
        body: {
          quoteId: quote.id,
          pricingInputGeneration: financing.pricingInputGeneration ?? 0,
          idempotencyKey,
        },
      },
      {
        onSuccess: (handoff) => {
          if (handoff.applicationUrl != null) {
            setHostedUrl(handoff.applicationUrl)
          }
        },
      },
    )
  }

  return (
    <div
      data-testid={
        hasApplication
          ? `battery-existing-loan-application`
          : `battery-sungage-reserve-now`
      }
      className="relative mt-7 flex flex-col items-start gap-3 print:hidden"
    >
      {start.isError ? (
        <p
          role="alert"
          data-testid="battery-sungage-initiate-error"
          className="max-w-md text-[length:var(--step--2)] text-white"
        >
          {isRefreshPaymentRequiredError(start.error)
            ? REFRESH_PAYMENT_REQUIRED_MESSAGE
            : sungageApplicationErrorMessage(start.error)}
        </p>
      ) : null}
      {uncertain ? (
        <p
          role="status"
          data-testid="battery-sungage-uncertain"
          className="max-w-md text-[length:var(--step--2)] text-white"
        >
          {SUNGAGE_UNCERTAIN_PROMPT}
        </p>
      ) : null}
      {hasApplication ? (
        <ApplicationStatusPill proposalId={proposalId} />
      ) : (
        <>
          <button
            type="button"
            disabled={!canStart || pending}
            onClick={onReserve}
            data-testid="battery-sungage-reserve-now-button"
            className={pillClass}
          >
            {pending ? SUNGAGE_RESERVE_NOW_BUSY_LABEL : RESERVE_NOW_LABEL}
            <span aria-hidden="true">→</span>
          </button>
          {reason != null && !pending ? (
            <p
              data-testid="battery-sungage-reserve-now-reason"
              className="text-[length:var(--step--2)] text-white/85"
            >
              {reason}
            </p>
          ) : null}
        </>
      )}
      {hostedUrl != null ? (
        <SungageHostedApplication
          url={hostedUrl}
          onClose={() => setHostedUrl(null)}
        />
      ) : null}
    </div>
  )
}
